import { SyncLoader } from "react-spinners";
import { Trans } from "react-i18next";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { useCallback } from "react";
import { useSendVerificationEmail } from "../domain/hooks/send_verification_email";
import { useLogout } from "../domain/hooks/use_logout";
import { useBarColor } from "../../../hooks/use_bar_color";
import { useTranslation } from "../../../../i18n/useTranslation";

interface LoadingPageProps {
    verified?: boolean,
    revalidationRequested?: boolean,
    error: boolean,
    isLoading: boolean,
    onRetry?: () => void
    email?: string
    banned?: boolean
}

const LoadingPage: React.FC<LoadingPageProps> = ({ verified, revalidationRequested, error, onRetry, isLoading, email, banned }) => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const { mutate: sendEmail, timer, isPending } = useSendVerificationEmail();
    const { mutate: logout, isPending: isLoggingOut } = useLogout();

    useBarColor('#FFFFFF');

    const handleLogout = useCallback(() => {
        if (isLoggingOut === false) {
            logout();
        }
    }, [isLoggingOut, logout]);

    return (
        <div
            className="fixed h-[100dvh] w-[100dvw] flex flex-col justify-center items-center gap-24"
            style={{ 
              background: `linear-gradient(25deg, ${theme.UvibesPinkGradient} 0%, ${theme.UvibesOrangeGradient} 75%)` 
            }}
        >
            <svg className="h-32.5 w-32.5" width="130" height="130" viewBox="0 0 130 130" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M74.4202 105.347C71.4802 109.838 68.7132 114.346 65.6502 118.669C63.4582 121.765 61.2962 124.938 57.8812 127.117C54.9992 128.956 51.7782 129.981 48.3972 130.009C43.1042 130.053 38.5012 128.233 34.8872 124.47C33.1872 122.7 32.0572 120.596 30.7722 118.579C28.443 114.928 26.1254 111.269 23.8192 107.603C22.0952 104.86 20.4212 102.089 18.6972 99.346C16.3752 95.649 14.0152 91.974 11.6862 88.281C10.5962 86.551 9.56217 84.796 8.48217 83.062C6.54617 79.958 4.61317 76.85 2.63917 73.764C0.680165 70.7 -0.153835 67.371 0.0231652 63.842C0.413165 56.05 6.38017 49.07 14.8002 47.746C20.5512 46.842 25.4552 48.266 29.7752 51.823C31.1892 52.985 32.3092 54.326 33.2452 55.831C35.5688 59.5691 37.8968 63.3044 40.2292 67.037C42.2432 70.26 44.2722 73.477 46.2942 76.7C46.9532 77.75 47.2572 77.748 47.9502 76.688C51.4572 71.32 54.9642 65.949 58.4792 60.585C61.0665 56.6357 63.6598 52.6883 66.2592 48.743C69.5292 43.778 74.1542 40.561 80.3112 39.407C85.6832 38.401 90.6182 39.482 95.0642 42.427C98.3702 44.619 100.576 47.574 101.785 51.197C103.598 56.629 102.861 61.763 99.7562 66.62C97.2122 70.597 94.6782 74.58 92.0592 78.518C88.6232 83.686 85.2052 88.863 81.8512 94.078C79.4372 97.834 76.9332 101.547 74.4202 105.347ZM123.268 79.625C123.525 80.921 123.499 82.145 123.499 83.368C123.496 95.163 123.502 106.961 123.499 118.756C123.499 120.545 123.186 122.293 122.262 123.92C120.677 126.712 118.032 128.405 114.401 129.194C110.35 130.075 106.818 129.231 103.703 127.15C101.211 125.482 99.7692 123.227 99.7632 120.53C99.7282 107.091 99.7432 93.653 99.7482 80.214C99.7512 75.972 104.402 71.811 109.525 71.354C112.266 71.11 114.882 71.301 117.322 72.424C120.724 73.984 122.656 76.384 123.268 79.625ZM94.1802 30.306C92.3942 27.773 91.4202 25.018 91.0662 22.081C90.7472 19.455 90.8632 16.822 91.6352 14.257C93.2592 8.85201 96.4932 4.66901 101.779 2.05501C106.946 -0.50099 112.279 -0.60099 117.603 1.35701C121.605 2.82901 124.645 5.52001 126.919 9.04001C129 12.26 130.028 15.79 130 19.5C129.965 24.076 128.606 28.306 125.532 31.967C122.277 35.845 118.178 38.313 113.039 38.931C107.59 39.585 102.604 38.345 98.3272 34.907C96.7082 33.605 95.2522 32.16 94.1812 30.305"
                    fill="white"
                />
            </svg>

            {
                isLoading
                    ? <SyncLoader speedMultiplier={0.5} size={20} color="white" className="z-1"/>
                    : banned === true
                        ? <div className="flex flex-col items-center gap-10 px-3">
                            <p className="font-medium text-center text-sm text-white">
                                <Trans
                                    i18nKey="access.banned"
                                    components={{ mail: <a href="mailto:support@uvibes.fr" className="underline" /> }}
                                />
                            </p>
                            <button
                                className="rounded-full text-white underline font-medium text-sm px-6 py-2.25 min-h-10.5 min-w-51 cursor-pointer"
                                onClick={handleLogout}
                            >
                                {t('common.button.logout')}
                            </button>
                        </div>
                    : verified === false
                        ? <div className="flex flex-col items-center gap-10 px-3">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="flex flex-col items-center gap-2.5">
                                        <p className="font-medium text-center text-sm text-white">{t('access.verify_email')}</p>
                                        {revalidationRequested && (
                                            <p className="font-medium text-center text-sm text-white">{t('access.revalidation_warning')}</p>
                                        )}
                                        <div className="flex flex-col items-center gap-1.25">
                                            <p className="text-center text-xs text-white">{t('access.check_inbox')}</p>
                                            <p className="text-center text-xs text-white">
                                                {`${t('access.nothing_received')} `}
                                                <span
                                                    className={`underline font-medium ${timer || isPending ? "cursor-not-allowed opacity-75" : "cursor-pointer"}`}
                                                    onClick={() => {
                                                        if (!timer && email && !isPending) {
                                                            sendEmail(email!)
                                                        }
                                                    }}
                                                >
                                                    {t('access.resend_email')}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        className="rounded-full bg-white font-bold px-6 py-2.25 min-h-10.5 min-w-51 cursor-pointer"
                                        style={{ color: theme.UvibesPink }}
                                        onClick={onRetry}
                                    >
                                        {t('access.email_verified')}
                                    </button>
                                </div>
                                <div className="flex flex-col items-center gap-1.25">
                                    <p className="text-center text-xs text-white">
                                        <Trans
                                            i18nKey="access.contact_issue"
                                            components={{ mail: <a href="mailto:contact@uvibes.fr" className="font-semibold underline" /> }}
                                        />
                                    </p>
                                    <button
                                        className="rounded-full text-white underline font-medium text-sm px-6 py-2.25 min-h-10.5 min-w-51 cursor-pointer"
                                        onClick={handleLogout}
                                    >
                                        {t('common.button.logout')}
                                    </button>
                                </div>
                            </div>
                        : error &&  <div className="flex flex-col items-center gap-4 px-3">
                                <p className="font-medium text-center text-sm text-white">{t('access.profile_error')}</p>
                                <button
                                    className="rounded-full bg-white font-bold px-6 py-2.25 min-h-10.5 min-w-51 cursor-pointer"
                                    style={{ color: theme.UvibesPink }}
                                    onClick={onRetry}
                                >
                                    {t('common.button.retry')}
                                </button>
                            </div>
            }
        </div>
    );
}

export default LoadingPage;